import { Link } from "react-router-dom";
import { ArrowRight, Heart } from "lucide-react";
import { IMAGES } from "../data/products";
import useSiteImages from "../hooks/useSiteImages";
import Reveal from "../components/Reveal";

export default function About() {
  const siteImages = useSiteImages();

  return (
    <div data-testid="about-page" className="bg-shay-cream">
      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-8 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <Reveal>
              <p className="eyebrow">Our Story</p>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-shay-navy mt-4 tracking-tight leading-[0.95]">
                Baking memories
                <br /> one <span className="italic text-shay-gold">batch</span> at a time.
              </h1>
              <p className="mt-7 text-shay-chocolate/80 text-lg leading-relaxed max-w-lg">
                Shay Cooks LLC was created from a passion for bringing people together through fresh baked desserts.
                Every cookie and brownie is handcrafted in small batches using quality ingredients — and baked with care.
              </p>
              <p className="mt-4 text-shay-chocolate/80 text-lg leading-relaxed max-w-lg">
                The goal is simple: create treats that make people smile.
              </p>
              <div className="mt-9 flex flex-wrap gap-4">
                <Link to="/menu" className="btn-gold" data-testid="about-order-btn">
                  Try The Menu <ArrowRight size={16} />
                </Link>
                <Link to="/contact" className="btn-ghost-navy" data-testid="about-contact-btn">
                  Say Hello
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-6">
            <Reveal delay={0.2}>
              <div className="relative aspect-[4/5] zoom-on-hover">
                <img src={siteImages.about_baker || IMAGES.baker} alt="Shay in the kitchen" className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" />
                <span className="absolute -bottom-6 -left-6 font-script text-shay-gold text-7xl md:text-8xl">
                  Shay
                </span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-shay-cream-deep py-20 md:py-28 paper-noise relative">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <p className="eyebrow">What We Stand For</p>
              <h2 className="font-display text-4xl md:text-5xl text-shay-navy mt-3 tracking-tight">
                Quality you can <span className="italic text-shay-brown">taste.</span>
              </h2>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-10 md:gap-16">
            {[
              { title: "Small Batch", body: "We never scale up at the cost of quality. Every batch is small, every cookie inspected." },
              { title: "Real Ingredients", body: "Real butter, real chocolate, real love. No shortcuts, no mystery ingredients." },
              { title: "Baked Today", body: "Your order is mixed, rolled, and baked the day it ships — never sitting on a shelf." },
            ].map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <div className="text-center md:text-left">
                  <span className="font-script text-shay-gold text-5xl">0{i + 1}</span>
                  <h3 className="font-display text-2xl md:text-3xl text-shay-navy mt-2 tracking-tight">{s.title}</h3>
                  <p className="mt-4 text-shay-chocolate/75 leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Photo strip */}
      <section className="bg-shay-cream py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-8 grid md:grid-cols-12 gap-6 md:gap-8">
          <Reveal>
            <div className="md:col-span-5 zoom-on-hover aspect-[4/5]">
              <img src={siteImages.about_story || IMAGES.handsBreaking} alt="Hands breaking a cookie" className="w-full h-full object-cover" loading="lazy" decoding="async" />
            </div>
          </Reveal>
          <div className="md:col-span-7 flex flex-col justify-center">
            <Reveal delay={0.1}>
              <Heart className="text-shay-gold mb-5" size={28} />
              <h3 className="font-display text-3xl md:text-5xl text-shay-navy tracking-tight leading-[1.05]">
                "If it's not the best thing you ate this week, I haven't done my job."
              </h3>
              <p className="font-script text-shay-brown text-2xl mt-6">— Shay</p>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
